// @ts-strict-ignore
import EnterAmount from './EnterAmount'
import { useCartReferenceStore } from '@components/LoadCart'
import { useLocalization } from '@components/Localization'
import { useNotifications } from '@components/NotificationsProvider'
import QuickCart from '@components/_product-detail/QuickCart'
import { Button } from '@gassan-ui'
import { useAddGiftcardToCartMutation, useRemoveItemFromCartMutation } from '@generated'
import { dataLayerAddToCart } from '@lib/data-layer-social-ads/add-to-cart'
import { genericPushToTrackingLayer } from '@lib/data-layer-social-ads/generic-push-to-tracking-layer'
import { articlesToDataLayerItems } from '@lib/data-layer-social-ads/helpers/articlesToDataLayerItems'
import { useCart } from '@lib/hooks/use-cart'
import pinterestTracking from '@lib/pinterest-tracking'
import { GIFTCARD_ID } from '@pages/giftcard'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, useState } from 'react'

type Props = {
  articleId: string
}

const AddToCart: FC<Props> = ({ articleId }) => {
  const [amount, setAmount] = useState<number>(0)
  const [showQuickCart, setShowQuickCart] = useState(false)
  const { language, parseGraphQlError } = useLocalization()
  const { t } = useTranslation(['other', 'navigation', 'notifications', 'product-detail'])
  const notifications = useNotifications()
  const router = useRouter()
  const { cart } = useCart()
  const cartReferenceStore = useCartReferenceStore()
  const reference = cartReferenceStore.reference
  const [, removeItemFromCart] = useRemoveItemFromCartMutation()
  const [result, addGiftcardToCart] = useAddGiftcardToCartMutation()

  const handleRemoveItemFromCart = async (
    cartItemId: string,
    unitPrice: number,
    quantity: number,
  ) => {
    if (!cart) {
      return
    }
    const item = cart.items.find((item) => item.id === cartItemId && item.unitPrice === unitPrice)

    try {
      await removeItemFromCart({ reference, cartItemId })
      if (cart.items.length === 1) {
        setShowQuickCart(false)
      }
      notifications.addNotification({
        variant: 'icon-action-undo',
        label: item.article.brand,
        sublabel: item.article.description,
        description: t('removedFromShoppingCart', { ns: 'other' }),
        actionLabel: t('undo', { ns: 'other' }),
        onClickAction: () => handleaddGiftcardToCart(item.article.id, quantity),
      })
      genericPushToTrackingLayer('Ecommerce', 'removeFromCart')
    } catch (error) {
      notifications.addNotification({
        variant: 'error',
        label: t('refreshAndTryAgain', { ns: 'notifications' }),
      })
    }
  }

  const handleaddGiftcardToCart = async (articleId: string, quantity = 1) => {
    try {
      const { data, error } = await addGiftcardToCart({
        reference,
        amount,
        quantity,
      })

      if (error) {
        return notifications.addNotification({
          variant: 'error',
          label: parseGraphQlError(error.message),
        })
      }

      if (data) {
        const article = data?.addGiftcardToCart?.items.find(
          (item) => item.articleId === GIFTCARD_ID && item.unitPrice === amount * 100,
        )
        const prefix = `/${language}`
        const dataLayerItem = articlesToDataLayerItems([article.article] as any)
        dataLayerAddToCart(dataLayerItem[0])
        genericPushToTrackingLayer('Ecommerce', 'addToCart')
        pinterestTracking.addToCart({
          product_id: articleId,
          product_quantity: quantity,
        })
        if (window.innerWidth <= 640) {
          notifications.addNotification({
            variant: 'confirmation',
            label: article.article.brand,
            sublabel: article.description,
            description: t('addedToShoppingBag', { ns: 'other' }),
            actionLabel: t('view', { ns: 'other' }),
            onClickAction: () =>
              router.push(
                `/[...slug]`,
                `${prefix}${t('secondaryNav.cart.url', { ns: 'navigation' })}`,
              ),
          })
        } else {
          setShowQuickCart(true)
        }
        setAmount(0)
      }
    } catch (error) {
      notifications.addNotification({
        variant: 'error',
        label: t('refreshAndTryAgain', { ns: 'notifications' }),
      })
    }
  }

  const handleOrderClick = () => {
    handleaddGiftcardToCart(articleId)
  }

  return (
    <>
      <EnterAmount value={amount} handleChange={(val) => setAmount(val)} />

      <Button
        status={amount < 20 || amount > 1000 ? 'disabled' : result.fetching ? 'loading' : 'idle'}
        variant="dark"
        data-test-id="add-to-cart-button"
        onClick={() => handleOrderClick()}
      >
        {t('orderOnline', { ns: 'product-detail' })}
      </Button>

      {showQuickCart && (
        <QuickCart
          onRequestClose={() => setShowQuickCart(false)}
          handleRemoveItemFromCart={(id, unitPrice, quantity) =>
            handleRemoveItemFromCart(id, unitPrice, quantity)
          }
        />
      )}
    </>
  )
}

export default AddToCart
